/**
 * `<pg-donut-kpi>` — indicador de KPI em formato de anel (donut),
 * reutilizável em qualquer dashboard de perfil (CEO/Sócio/Financeiro —
 * mesmo critério de reuso de `pg-kpi-card`, CODING_STANDARDS.md, seção
 * 16). Nunca calcula nem infere dado: a tela que consome decide
 * `valor`/`percentual`/`segmentos` a partir de dado real já carregado
 * (regra de produto: nunca inventar número pra preencher a interface).
 *
 * Duas formas de desenhar o anel, escolhidas pelo que for informado:
 * - `percentual` (0-100): um único arco de progresso (gauge) — uso
 *   típico de "participação societária", "% de parceiros ativos" etc.
 * - `segmentos` (propriedade JS, 2+ fatias com valor real): distribuição
 *   multi-fatia, mesma técnica de `pg-chart` tipo `donut`.
 * - Nenhum dos dois informado: o anel desenha só a trilha de fundo,
 *   sem arco colorido — usado quando a métrica é um valor absoluto sem
 *   denominador real pra virar proporção (ex: "pedidos pagos no mês"),
 *   pra nunca sugerir uma fração que não existe.
 *
 * `estado`: 'ok' (padrão) | 'carregando' | 'vazio' | 'indisponivel' —
 * mesmo contrato de `pg-kpi-card`. IMPORTANTE: um dado real que resulta
 * em zero (ex: "nenhum bônus pago ainda", percentual 0%) é `estado="ok"`
 * com `valor`/`percentual` igual a zero — NUNCA `estado="vazio"`, que é
 * reservado para "não existe registro nenhum pra calcular isso". Essa
 * distinção evita inventar um percentual mas também evita esconder um
 * zero legítimo atrás de um estado de "sem dado".
 *
 * Atributos: `titulo`, `valor` (já formatado, mostrado no centro do
 * anel), `percentual` (número 0-100, opcional), `dica` (legenda curta
 * abaixo do anel), `estado`, `cor` (token: 'roxo'|'azul'|'verde'|
 * 'laranja'|'dourado'|'erro' — controla o gradiente do arco/glow),
 * `tendencia`/`tendencia-direcao` (mesmo padrão de `pg-kpi-card`, só
 * exibido quando a tela tem uma série real para calculá-lo).
 *
 * Propriedade JS: `segmentos` (`PgDonutKpiSegmento[]`, opcional).
 *
 * Uso:
 * ```html
 * <pg-donut-kpi titulo="Participação societária" valor="12%" percentual="12" cor="roxo"></pg-donut-kpi>
 * <pg-donut-kpi titulo="Pedidos pagos (mês)" valor="47" cor="azul"></pg-donut-kpi>
 * <pg-donut-kpi titulo="Taxa de conversão" estado="indisponivel" dica="não há domínio de tráfego/leads rastreado hoje"></pg-donut-kpi>
 * ```
 */
const CORES_TOKEN = {
    roxo: 'var(--pg-color-roxo, #8b5cf6)',
    azul: 'var(--pg-color-destaque, #2f8cff)',
    verde: 'var(--pg-color-sucesso, #3bb273)',
    laranja: 'var(--pg-color-alerta, #e0a52c)',
    dourado: 'var(--pg-color-dourado, #c9a24b)',
    erro: 'var(--pg-color-erro, #e5484d)',
};
export class PgDonutKpi extends HTMLElement {
    static get observedAttributes() {
        return ['titulo', 'valor', 'percentual', 'dica', 'estado', 'cor', 'tendencia', 'tendencia-direcao'];
    }
    root;
    _segmentos = [];
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    get segmentos() {
        return this._segmentos;
    }
    set segmentos(valor) {
        this._segmentos = valor;
        this.render();
    }
    connectedCallback() {
        this.render();
    }
    attributeChangedCallback() {
        this.render();
    }
    render() {
        const titulo = this.getAttribute('titulo') ?? '';
        const dica = this.getAttribute('dica') ?? '';
        const estado = this.getAttribute('estado') ?? 'ok';
        const corChave = this.getAttribute('cor') ?? 'azul';
        const cor = CORES_TOKEN[corChave] ?? CORES_TOKEN.azul;
        const tendencia = this.getAttribute('tendencia') ?? '';
        const tendenciaDirecao = this.getAttribute('tendencia-direcao') ?? 'neutra';
        const percentualAttr = this.getAttribute('percentual');
        const percentualBruto = percentualAttr !== null && percentualAttr !== '' ? Number(percentualAttr) : null;
        const percentual = percentualBruto !== null && Number.isFinite(percentualBruto) ? Math.max(0, Math.min(100, percentualBruto)) : null;
        const valorExibido = estado === 'ok' ? (this.getAttribute('valor') ?? '—') : '—';
        const dicaExibida = estado === 'indisponivel' ? dica || 'módulo não implementado' : estado === 'carregando' ? '' : dica;
        const mostrarTendencia = estado === 'ok' && !!tendencia;
        const anel = estado === 'carregando'
            ? `<div class="skeleton-anel" aria-hidden="true"></div>`
            : svgAnel({
                percentual: estado === 'ok' ? percentual : null,
                segmentos: estado === 'ok' ? this._segmentos : [],
                cor,
                valorCentro: valorExibido,
                opaco: estado !== 'ok',
            });
        this.root.innerHTML = `
      <style>
        :host { display: block; }
        .card {
          background: linear-gradient(165deg, var(--pg-color-bg-elevado, #1a1d21) 0%, color-mix(in srgb, var(--pg-color-bg-elevado, #1a1d21) 94%, black) 100%);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-lg, 16px);
          padding: var(--pg-space-5, 24px) var(--pg-space-4, 16px);
          font-family: var(--pg-font-familia-base, system-ui);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--pg-space-3, 12px);
          text-align: center;
          box-shadow: var(--pg-shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.4));
          transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
          animation: pg-donut-entrar 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
          height: 100%;
          box-sizing: border-box;
        }
        @keyframes pg-donut-entrar { from { opacity: 0; transform: translateY(6px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @media (prefers-reduced-motion: reduce) { .card { animation: none; } }
        .card:hover {
          border-color: color-mix(in srgb, ${cor} 45%, var(--pg-color-borda, #2a2f36));
          box-shadow: 0 8px 20px -8px color-mix(in srgb, ${cor} 40%, transparent), var(--pg-shadow-md, 0 4px 12px rgba(0, 0, 0, 0.35));
          transform: translateY(-2px);
        }
        .titulo {
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        .anel-wrap { position: relative; width: 112px; height: 112px; flex-shrink: 0; }
        .anel-svg { width: 100%; height: 100%; display: block; filter: drop-shadow(0 0 8px color-mix(in srgb, ${cor} 26%, transparent)); transition: filter 0.2s ease; }
        .card:hover .anel-svg { filter: drop-shadow(0 0 13px color-mix(in srgb, ${cor} 38%, transparent)); }
        .anel-arco { animation: pg-donut-arco 0.7s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        @keyframes pg-donut-arco { from { stroke-dasharray: 0 999; } }
        @media (prefers-reduced-motion: reduce) { .anel-arco { animation: none; } }
        .anel-valor { font-family: var(--pg-font-familia-display, system-ui); font-weight: var(--pg-font-weight-bold, 700); fill: var(--pg-color-texto-primario, #f2f3f5); font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
        .anel-valor.opaco { fill: var(--pg-color-texto-desabilitado, #5c626b); }
        .skeleton-anel {
          width: 100%; height: 100%; border-radius: 50%;
          background: linear-gradient(90deg, var(--pg-color-bg-hover, #22262b) 25%, var(--pg-color-borda, #2a2f36) 50%, var(--pg-color-bg-hover, #22262b) 75%);
          background-size: 200% 100%;
          animation: pg-skeleton-varredura 1.4s ease-in-out infinite;
        }
        @keyframes pg-skeleton-varredura { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @media (prefers-reduced-motion: reduce) { .skeleton-anel { animation: none; } }
        .rodape { display: flex; flex-direction: column; align-items: center; gap: 4px; min-height: 1.1em; }
        .dica { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .dica:empty { display: none; }
        .tendencia { display: inline-flex; align-items: center; gap: 3px; font-size: var(--pg-font-size-xs, 0.75rem); font-weight: var(--pg-font-weight-semibold, 600); }
        .tendencia.alta { color: var(--pg-color-sucesso, #3bb273); }
        .tendencia.baixa { color: var(--pg-color-erro, #e5484d); }
        .tendencia.neutra { color: var(--pg-color-texto-secundario, #9aa1ab); }
      </style>
      <div class="card" part="card">
        <span class="titulo">${escaparHtml(titulo)}</span>
        <div class="anel-wrap">${anel}</div>
        ${estado === 'carregando'
            ? ''
            : `<div class="rodape">
                 ${dicaExibida ? `<span class="dica">${escaparHtml(dicaExibida)}</span>` : ''}
                 ${mostrarTendencia ? `<span class="tendencia ${tendenciaDirecao}">${svgSetaTendencia(tendenciaDirecao)}${escaparHtml(tendencia)}</span>` : ''}
               </div>`}
      </div>
    `;
    }
}
function svgAnel(opts) {
    const raio = 15.9155; // circunferência = 100 (mesma técnica de pg-chart)
    const circunferencia = 2 * Math.PI * raio;
    const idBase = Math.round(Math.random() * 1e6);
    const defs = [];
    let arco = '';
    const segmentosValidos = opts.segmentos.filter((s) => s.valor > 0);
    if (segmentosValidos.length > 0) {
        const total = segmentosValidos.reduce((soma, s) => soma + s.valor, 0);
        const gap = segmentosValidos.length > 1 ? 1.6 : 0;
        let acumulado = 0;
        arco = segmentosValidos
            .map((s, i) => {
            const fracao = s.valor / total;
            const comprimento = fracao * circunferencia;
            const comprimentoVisual = Math.max(0.6, comprimento - gap);
            const dasharray = `${comprimentoVisual} ${circunferencia - comprimentoVisual}`;
            const dashoffset = -(acumulado * circunferencia + gap / 2);
            acumulado += fracao;
            const corSegmento = s.cor ?? opts.cor;
            const idGradiente = `pg-donut-grad-${idBase}-${i}`;
            defs.push(gradienteAnel(idGradiente, corSegmento));
            return `<circle class="anel-arco" cx="21" cy="21" r="${raio}" fill="none" stroke="url(#${idGradiente})" stroke-width="6.5" stroke-linecap="round" stroke-dasharray="${dasharray}" stroke-dashoffset="${dashoffset}"><title>${escaparHtml(s.label)}</title></circle>`;
        })
            .join('');
    }
    else if (opts.percentual !== null && opts.percentual > 0) {
        const idGradiente = `pg-donut-grad-${idBase}`;
        defs.push(gradienteAnel(idGradiente, opts.cor));
        const comprimento = (opts.percentual / 100) * circunferencia;
        const comprimentoVisual = Math.min(comprimento, circunferencia - 0.01);
        arco = `<circle class="anel-arco" cx="21" cy="21" r="${raio}" fill="none" stroke="url(#${idGradiente})" stroke-width="6.5" stroke-linecap="round" stroke-dasharray="${comprimentoVisual} ${circunferencia - comprimentoVisual}" />`;
    }
    const tamanhoFonte = tamanhoFonteCentro(opts.valorCentro);
    return `
    <svg class="anel-svg" viewBox="0 0 42 42">
      <defs>${defs.join('')}</defs>
      <circle cx="21" cy="21" r="${raio}" fill="none" stroke="var(--pg-color-bg-base, #121417)" stroke-width="6.5" />
      <g transform="rotate(-90 21 21)">${arco}</g>
      <text x="21" y="23.3" text-anchor="middle" class="anel-valor${opts.opaco ? ' opaco' : ''}" style="font-size:${tamanhoFonte}px">${escaparHtml(opts.valorCentro)}</text>
    </svg>`;
}
function gradienteAnel(id, cor) {
    return `<linearGradient id="${id}" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="color-mix(in srgb, ${cor} 82%, white)" /><stop offset="100%" stop-color="color-mix(in srgb, ${cor} 85%, black)" /></linearGradient>`;
}
/** Heurística simples de tamanho pra caber no centro do anel (42x42 de viewBox) sem medir texto de verdade — suficiente pra strings curtas tipo "12%"/"47"/"R$ 320,00". */
function tamanhoFonteCentro(valor) {
    const n = valor.length;
    if (n <= 3)
        return 9;
    if (n <= 5)
        return 7.5;
    if (n <= 8)
        return 6;
    if (n <= 12)
        return 4.6;
    return 3.6;
}
function svgSetaTendencia(direcao) {
    if (direcao === 'alta') {
        return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="18 15 12 9 6 15" /></svg>';
    }
    if (direcao === 'baixa') {
        return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>';
    }
    return '';
}
function escaparHtml(valor) {
    const div = document.createElement('div');
    div.textContent = valor;
    return div.innerHTML;
}
customElements.define('pg-donut-kpi', PgDonutKpi);
//# sourceMappingURL=pg-donut-kpi.js.map